// Shared call-history formatting, used by the Main screen's Recents and the
// contact-detail per-contact log. Kept in one place so both render outcomes
// identically (docs/06 §2).

import type { CallLogEntry } from '@merkury/protocol';
import { getLang, translate } from '../../i18n/index.js';

export function formatTimestamp(iso: string): string {
  try {
    return new Intl.DateTimeFormat(getLang(), { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}

function formatCallDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Live call-timer formatting (InCallScreen's ticking duration, and the
 * shell's return-to-call banner): `h:mm:ss` once past an hour, `m:ss`
 * otherwise. Distinct from `formatCallDuration` above, which rounds a fixed
 * historical duration for the call log rather than a running elapsed time.
 */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
}

/**
 * A completed call shows its duration, everything else names the outcome — from
 * this user's side ("no answer" when your own outgoing ring expired, "missed"
 * when you didn't pick up).
 */
export function describeCall(entry: CallLogEntry): string {
  const arrow = entry.direction === 'outgoing' ? '↗' : '↙';
  if (entry.outcome === 'completed') {
    const duration = entry.endedAt
      ? formatCallDuration(new Date(entry.endedAt).getTime() - new Date(entry.startedAt).getTime())
      : '';
    return `${arrow} ${duration}`.trim();
  }
  const lang = getLang();
  const label = {
    missed: translate(lang, entry.direction === 'outgoing' ? 'callFormat.noAnswer' : 'callFormat.missed'),
    declined: translate(lang, 'callFormat.declined'),
    failed: translate(lang, 'callFormat.failed'),
  }[entry.outcome];
  return `${arrow} ${label}`;
}
